import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, ArrowRight } from "lucide-react";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Reveal } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import priserHero from "@/assets/priser-hero.jpg?w=1600&format=avif;webp;jpg&as=picture";
import { Picture } from "@/components/site/Picture";
import { aeoGeo } from "@/lib/seo/schema";

const SITE_ORIGIN = "https://fenny.se";

export const Route = createFileRoute("/en_/pricing")({
  head: () => ({
    meta: [
      { title: "Video pricing — subscriptions and projects | Fenny" },
      { name: "description", content: "Transparent pricing for Pulse (subscription) and Prime (fixed-fee projects). No hourly billing — just clear deliverables and a 14-day first cut." },
      { property: "og:title", content: "Video pricing — subscriptions and projects | Fenny" },
      { property: "og:description", content: "Transparent pricing. No hourly billing." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/en/pricing" },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Video pricing — subscriptions and projects | Fenny" },
      { name: "language", content: "en" },
    ],
    links: [
      { rel: "canonical", href: "https://fenny.se/en/pricing" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/pricing", name: "Video pricing — subscriptions and projects | Fenny", description: "Transparent pricing for Pulse (subscription) and Prime (fixed-fee projects). No hourly billing — just clear deliverables and a 14-day first cut."})) },
    ],
  }),
  component: EnPricingPage,
});

const tiers = [
  {
    name: "Pulse Lite",
    price: "EUR 1,395",
    period: "/ month",
    blurb: "For teams getting serious about video — a steady drumbeat without overhead.",
    items: ["1 shoot day every other month", "2 finished clips / month", "All channel formats", "Shared planning"],
    tone: "outline" as const,
  },
  {
    name: "Pulse Core",
    price: "EUR 3,195",
    period: "/ month",
    blurb: "The most-picked tier — own your content engine without the in-house overhead.",
    items: ["1 shoot day / month", "4 finished clips / month", "Strategy + quarterly plan", "Priority slot in production"],
    tone: "mint" as const,
    featured: true,
  },
  {
    name: "Pulse Pro",
    price: "EUR 4,195",
    period: "/ month",
    blurb: "For brands publishing weekly — full content machine, premium quality.",
    items: ["1–2 shoot days / month", "6+ finished clips / month", "Dedicated producer", "Light Prime credits included"],
    tone: "outline" as const,
  },
];

function EnPricingPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-white/5 pt-44 pb-20 md:pt-56 md:pb-24">
        <Picture data={priserHero} alt="Fenny film crew on set — cover image for video production pricing" loading="eager" fetchPriority="high" className="absolute inset-0 -z-20 h-full w-full object-cover object-[72%_center] md:object-[70%_center]" />
        <div aria-hidden className="absolute inset-0 -z-10" style={{ background: "linear-gradient(105deg, color-mix(in oklab, var(--background) 94%, transparent) 0%, color-mix(in oklab, var(--background) 78%, transparent) 45%, color-mix(in oklab, var(--background) 36%, transparent) 80%, transparent 100%)" }} />
        <div aria-hidden className="absolute inset-x-0 bottom-0 -z-10 h-32" style={{ background: "linear-gradient(to bottom, transparent 0%, var(--background) 100%)" }} />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="max-w-4xl">
            <Reveal>
              <div className="mb-6 text-[11px] uppercase tracking-[0.18em] text-butter">Pricing</div>
            </Reveal>
            <KineticHeading
              text="No hours. No surprises. Just clarity."
              className="text-5xl font-semibold leading-[1.02] md:text-7xl lg:text-[5.25rem]"
              highlight={{ indices: [4], className: "text-butter" }}
            />
            <Reveal delay={0.4}>
              <p className="mt-8 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl">
                Three tiers for a steady video flow. Two alternative ways in. Everything on the table —
                so you can choose with your head, not your gut.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <Section>
        <EyebrowHeading eyebrow="Subscriptions" title="Pick the pace. We'll match the ambition." lede="Lite to get going, Core to own the flow, Pro to dominate." align="center" />
        <div className="mx-auto mt-14 grid max-w-5xl gap-5 md:grid-cols-3">
          {tiers.map((t) => (
            <Reveal key={t.name}>
              <article className={`flex h-full flex-col rounded-3xl border bg-surface p-7 ${t.featured ? "border-mint/40 shadow-[0_20px_60px_-30px_rgba(60,200,150,0.4)]" : "border-white/10"}`}>
                {t.featured && <div className="mb-3 inline-flex w-fit rounded-full bg-mint/15 px-3 py-1 text-[11px] uppercase tracking-widest text-mint">Most picked</div>}
                <h3 className="text-xl font-semibold">{t.name}</h3>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-serif text-4xl font-semibold leading-none text-foreground">{t.price}</span>
                  <span className="text-sm text-muted-foreground">{t.period}</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.blurb}</p>
                <ul className="mt-5 space-y-2.5">
                  {t.items.map((i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/90">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex-1" />
                <Button to="/en/contact" variant={t.featured ? "mint" : "outline"} arrow className="w-full">Start {t.name.split(" ")[1]}</Button>
              </article>
            </Reveal>
          ))}
        </div>
        <p className="mx-auto mt-10 max-w-3xl text-center text-xs leading-relaxed text-muted-foreground">
          3-month notice. 1-month notice to change tier. Travel, lodging and specialist gear are invoiced
          when needed — always transparent before you approve.
        </p>
      </Section>

      {/* Alt routes */}
      <Section className="bg-surface/40">
        <EyebrowHeading eyebrow="Alternative ways in" title="Not ready for a subscription? Need something bigger?" align="center" />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <article className="flex flex-col rounded-3xl border border-white/10 bg-surface p-8">
            <div className="text-sm font-semibold uppercase tracking-widest text-mint">
              Pilot
              <span className="ml-2 text-xs font-normal normal-case tracking-normal text-muted-foreground">· one-off, fixed price</span>
            </div>
            <div className="mt-4 font-serif text-4xl font-semibold leading-tight text-mint">EUR 2,795</div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Production meetings, one full shoot day, delivery of 2–3 clips in the formats you need.
              Perfect for experiencing how it is to work with Fenny before committing.{" "}
              <em className="text-foreground/80">Available once per client.</em>
            </p>
            <div className="mt-8 flex-1" />
            <Button to="/en/contact" variant="outline" arrow className="w-full">Start with Pilot</Button>
          </article>

          <article className="flex flex-col rounded-3xl border border-butter/30 bg-surface p-8">
            <div className="text-sm font-semibold uppercase tracking-widest text-butter">
              Prime
              <span className="ml-2 text-xs font-normal normal-case tracking-normal text-muted-foreground">· large-scale via quote</span>
            </div>
            <div className="mt-4 font-serif text-4xl font-semibold leading-tight text-butter">Tailor-made</div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Commercials, documentary, TV series, live broadcasts and larger brand productions.
              Creative power at the highest level.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Typical budgets: from EUR 14,000 up to major campaigns. We quote after a strategy call
              where we understand scope and ambition.
            </p>
            <div className="mt-8 flex-1" />
            <Button to="/en/prime" variant="outline" arrow className="w-full">Read about Prime</Button>
          </article>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="pt-0">
        <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-gradient-to-br from-surface via-surface to-butter/[0.05] p-10 text-center md:p-14">
          <div className="text-[11px] uppercase tracking-[0.2em] text-butter">One last question?</div>
          <h2 className="mt-3 text-balance text-3xl font-semibold md:text-4xl">Grab us for a 15-min check.</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Something unclear? We'll sort packages, formats or just whether we're the right match — no
            sales pitch. Premium starts with talking to a human.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Magnetic>
              <Button to="/en/contact" variant="mint" arrow>Book 15 min</Button>
            </Magnetic>
            <Link to="/en" className="inline-flex items-center gap-2 text-sm text-foreground/70 underline-offset-4 hover:text-mint hover:underline">
              Back to overview <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
